import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { useCallback, useId, useMemo, useState } from "react";

import { actionTooltipProps } from "../../utils/action-tooltip-props";
import styles from "./disabled-tooltip.module.scss";
import type { DisabledTooltipProps } from "./types-disabled-tooltip";

/**
 * Tooltip do DS para o que não abre o do Prime sozinho (botão ou item desabilitado) e para o motivo que antes ia no `title` nativo (tag, item de lista):
 * o `<span>` do React recebe o hover e o elemento fica dentro dele.
 * No lugar do `showOnDisabled` do Prime, que move o botão para um `div` criado fora do React e faz o `removeChild` do React falhar.
 * O span fica sempre, com ou sem texto, para o botão não trocar de pai quando o tooltip liga e desliga.
 * Botão habilitado também abre por foco (`event: "both"` do `actionTooltipProps`): o `focus` não sobe para o span, então o filho direto é alvo também.
 */
export function DisabledTooltip({
  content,
  position = "top",
  className,
  children,
  ...rest
}: DisabledTooltipProps) {
  const id = useId();
  const [montado, setMontado] = useState(false);
  const marcar = useCallback((el: HTMLSpanElement | null) => setMontado(el !== null), []);
  const alvos = useMemo(
    () => [`[data-disabled-tooltip="${id}"]`, `[data-disabled-tooltip="${id}"] > *`],
    [id],
  );

  return (
    <>
      <span
        ref={marcar}
        data-disabled-tooltip={id}
        className={classNames(styles.alvo, className)}
        {...rest}
      >
        {children}
      </span>
      {content && montado ? (
        <Tooltip target={alvos} {...actionTooltipProps(content, { position })} />
      ) : null}
    </>
  );
}
DisabledTooltip.displayName = "DisabledTooltip";
